import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    ViewChild
} from '@angular/core';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { Subscription } from 'rxjs';

import { CommonModule, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { StoreFinderComponentDef } from '@df/catalog-ui/store/finder/store-finder.component.def';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Store } from '@df/catalog/store/store';
import { Status } from '@df/core/status';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { ImageModule } from '@df/module-image/image.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TranslationModule } from '@df/ui/translation/translation.module';
import orderBy from 'lodash-es/orderBy';
import { GoogleMapComponent } from '../../module/google-maps/google-map.component';
import { ResultComponent } from '../../ui/api/result.component';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { Product } from '../product/product.decorator';
import { SEE_IN_STORE_DETAILS_MODAL_ID } from '../product/see-in-store/modal/store-details/product-view-see-in-store-modal-store-details.component';
import { SeeRangeInStoreStatusComponent } from '../see-range-in-store/status/see-range-in-store-status.component';
import { ProductViewSeeInStoreModalComponent } from './../product/see-in-store/modal/product-view-see-in-store-modal.component';
import { SeeRangeInStoreService } from './../see-range-in-store/see-range-in-store.service';
import { StoreOpenStatusComponent } from './store-open-status/store-open-status.component';
import { LocalStore } from './store.decorator';
@Component({
    selector: 'store-finder',
    templateUrl: 'store-finder.html',
    styleUrls: ['store-finder.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ResultComponent,
        TranslationModule,
        FormsModule,
        InputWrapComponent,
        NgClass,
        HrefComponent,
        CommonModule,
        StoreOpenStatusComponent,
        SeeRangeInStoreStatusComponent,
        ImageModule,
        OptionLabelPipe,
        Ga4TrackClickDirective,
        GridModule,
        GoogleMapComponent
    ]
})
export class StoreFinderComponent extends StoreFinderComponentDef implements OnInit, OnDestroy {
    loadSeeRangeInStoreStatus = new Status();
    selectedStore?: LocalStore | null;
    private _isMapVisible = false;
    @ViewChild('addressInput')
    addressInput?: ElementRef;
    @ViewChild('container')
    container!: ElementRef;
    @Output()
    storesUpdate = new EventEmitter<LocalStore[]>();

    @Input()
    showOnlyStores?: boolean;

    @Input()
    showStoresWithProductOrRangeAvailable?: boolean;

    private subscriptions = new Subscription();
    constructor(
        _ref: ChangeDetectorRef,
        @Optional()
        private productViewSeeInStoreModalComponent: ProductViewSeeInStoreModalComponent,
        private seeRangeInStoreService: SeeRangeInStoreService,
        private requestService: RequestService,
        private cmsModalService: ICmsModalService
    ) {
        super(_ref);
    }
    override ngOnInit(): void {
        this.subscriptions.add(this.loadSeeRangeInStoreStatus.subscribe(() => this.detectChanges()));
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
        this.loadAndShowAll();
    }
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
    get product(): Product<IProductDataV3> | undefined {
        return this.productViewSeeInStoreModalComponent?.product();
    }

    get isModal(): boolean {
        return !!this.productViewSeeInStoreModalComponent;
    }

    get isNarrowLayout(): boolean {
        if (this.isModal) {
            return true;
        }
        return this.client.sizeId < 2;
    }
    get isMapVisible(): boolean {
        return this._isMapVisible;
    }
    set isMapVisible(value: boolean) {
        if (this._isMapVisible !== value) {
            this._isMapVisible = value;
            this.detectChanges();
        }
    }
    showStoreDetails(store: LocalStore) {
        if (this.isModal) {
            this.cmsModalService.open(undefined, SEE_IN_STORE_DETAILS_MODAL_ID, undefined, store);
        } else {
            this.selectedStore = store;
            if (typeof window !== 'undefined') {
                window.scroll({ top: 0, behavior: 'smooth' });
            }
        }
        this.detectChanges();
    }
    hideStoreDetails() {
        this.selectedStore = null;
        this.detectChanges();
    }
    onStoreMarkerClicked(storeId: Event | number) {
        const store = this.findableModels?.find(store => store.id === storeId as any as number) as LocalStore;
        if (store) {
            this.selectedStore = store;
            this.container.nativeElement.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            this.isMapVisible = false;
            this.detectChanges();
        }
    }
    override onFindableModelsUpdate() {
        super.onFindableModelsUpdate();
        if (this.product) {
            const storesIds = this.findableModels?.map(i => i.id) || [];
            this.requestService.send(
                this.loadSeeRangeInStoreStatus,
                () =>
                    this.product &&
                    this.seeRangeInStoreService.loadSeeRangeInStoreModels(
                        this.product.id,
                        storesIds,
                        this.product?.data?.nyw_item_range_code_nh
                    )
            );
        }
        this.storesUpdate.emit(this.findableModels as LocalStore[]);
    }
    keyup(event: KeyboardEvent) {
        if (event.key === 'Enter' || event.code === 'Enter') {
            this.addressInput?.nativeElement.blur();
            this.search();
        }
    }
    override search(
        status?: Status,
        findData?: Catalog.IFindableLocateOptions | Catalog.IFindableLocateCoordsOptions,
        onError?: IRequestCallback
    ): void {
        this.selectedStore = null;
        super.search(status, findData, onError);
    }
    get displaySeeRange(): boolean {
        return !!(this.product && !this.loadSeeRangeInStoreStatus.busy);
    }

    get currentFindableModels() {
        if (this.showOnlyStores) {
            const models = this.findableModels?.filter(
                model => !(model.data?.url?.includes('warehouses') || model.data?.url?.includes('outlet'))
            );
            if (this.showStoresWithProductOrRangeAvailable && models) {
                const scoredModels = models.map(model => {
                    if (this.product) {
                        const isInStore = !!this.seeRangeInStoreService.getSeeRangeInStoreModel(this.product.id, model.id);

                        const rangeId = this.product.data?.nyw_item_range_code_nh;

                        const hasRangeInStore = !!(
                            !isInStore &&
                            rangeId &&
                            this.seeRangeInStoreService
                                .getSeeRangeInStoreRangeModels(rangeId, [model.id])
                                .some(i => i.data?.product && this.product && i.data.product !== this.product.id)
                        );

                        return { model, isInStore, hasRangeInStore };
                    }
                });
                return orderBy(scoredModels, ['isInStore', 'hasRangeInStore'], ['desc', 'desc']).map(i => i?.model);
            } else {
                return models;
            }
        } else {
            if (this.selectedStore) {
                const filteredModels = (this.findableModels || []).filter(model => model !== this.selectedStore);
                return [this.selectedStore, ...filteredModels];
            } else return this.findableModels;
        }
    }

    closeModal(): void {
        if (this.productViewSeeInStoreModalComponent) {
            this.productViewSeeInStoreModalComponent.close();
        }
    }

    getSeeRangeInStoreStatus(storeId: number): boolean {
        return !!(
            this.product?.data &&
            (this.seeRangeInStoreService.getSeeRangeInStoreModel(this.product.id, storeId) ||
                (this.product.data.nyw_item_range_code_nh &&
                    this.seeRangeInStoreService.getSeeRangeInStoreRangeModels(this.product.data.nyw_item_range_code_nh, [storeId]).length))
        );
    }

    trackBystoreId(index: number, store: LocalStore) {
        return store.id;
    }

    asLocalStore(store: Store): LocalStore {
        return store as LocalStore;
    }
}
